import {
  ApolloClient,
  ApolloLink,
  from,
  NormalizedCacheObject,
} from '@apollo/client';
import {
  SSRMultipartLink,
  InMemoryCache as NextSSRInMemoryCache,
  ApolloClient as NextSSRApolloClient,
} from '@apollo/experimental-nextjs-app-support';
import { getSession } from 'next-auth/react';

import {
  authLink,
  clientHttpLink,
  createCache,
  getApolloLink,
  requestIdLink,
  ssrHttpLink,
} from './base';
import { cartStateResolver } from '../../app/(purchase)/cart/state-management/state-management';
import {
  pdpConfigStateResolver,
  productsLowestPriceResolver,
} from '../../app/pdp-config/printer/state-management/pdp-config-state';
import { errorLink } from '../../graphql/apolloErrorLink';
import { getSSRMode } from '../getSSRMode';

function getAuthProvider(token: string): {
  getAccessToken(): Promise<string>;
} {
  return {
    getAccessToken: async () => token,
  };
}

const clientRef: { current: ApolloClient<NormalizedCacheObject> | null } = {
  current: null,
};

export function initializeApolloClient(appendLinks: ApolloLink[] = []) {
  const ssrLinks = [
    new SSRMultipartLink({
      stripDefer: true,
    }),
    ssrHttpLink(),
  ];
  const apolloClient =
    clientRef.current ??
    new ApolloClient({
      ssrMode: getSSRMode(),
      link: getApolloLink(ssrLinks, appendLinks),
      cache: createCache(),
    });

  return apolloClient;
}

export const getApolloClient = (token?: string) =>
  initializeApolloClient(token ? [authLink(getAuthProvider(token))] : []);

export const clientAuthless = new ApolloClient({
  link: from([
    requestIdLink,
    errorLink,
    clientHttpLink({ headers: { 'x-bypass-auth': 'true' } }),
  ]),
  cache: createCache(),
  name: 'web',
  connectToDevTools: process.env.NODE_ENV === 'development',
  ssrMode: getSSRMode(),
  headers: {
    'x-bypass-auth': 'true',
  },
});

export const clientCache = new NextSSRInMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        pdpConfig: {
          read: pdpConfigStateResolver,
        },
      },
    },

    Products: {
      fields: {
        items: {
          read: productsLowestPriceResolver,
        },
      },
    },
    Cart: {
      fields: {
        items: {
          read: cartStateResolver,
        },
      },
    },
  },
});

export const makeClient = () => {
  const authProvider = {
    getAccessToken: async () => (await getSession())?.access_token,
  };
  const link = authLink
    ? from([requestIdLink, errorLink, authLink(authProvider), clientHttpLink()])
    : from([requestIdLink, errorLink, clientHttpLink()]);
  return new NextSSRApolloClient({
    link:
      typeof window === 'undefined'
        ? ApolloLink.from([
            new SSRMultipartLink({
              stripDefer: true,
            }),
            link,
          ])
        : link,
    cache: clientCache,
    name: 'web',
    connectToDevTools: process.env.NODE_ENV === 'development',
    ssrMode: getSSRMode(),
    credentials: 'same-origin',
  });
};
